import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import flow from 'lodash/fp/flow';
import { convertFromRaw } from 'draft-js';
import axios from 'axios';

import { List, Button } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinnerThird } from '@fortawesome/pro-light-svg-icons';

import { useAuth } from 'hooks';
import { mentionReplace, lineBrakeToBr, isJSON, serializeToHtml } from 'lib/helper';
import { PROFILE_TYPES } from 'lib/constants';
import { getComments, resetComments } from 'store/actions/postsActions';
import { makeGetPostSelector, makeGetPostComments } from 'store/selectors/postSelectors';
import ProfileSwitch from 'components/Groups/NewPost/ProfileSwitch';

import CommentTextEditor from '../../Common/CommentTextEditor';
import Comment from '../Comment/Comment';

import {
  AvatarWrapper,
  BorderComments,
  CommentsContainer,
  EditorWrapper,
  commentsWrapper,
} from './CommentsStyles';

const getPost = makeGetPostSelector();
const getPostComments = makeGetPostComments();

const Comments = props => {
  const { postId, className = '', commentSinglePost, groupId } = props;
  const { t } = useTranslation();
  const [isCommentLoading, setIsCommentLoading] = useState(false);
  const pageSize = commentSinglePost ? 50 : 2;

  // const { userInfo, userPermissions } = useAuth();
  const { userInfo } = useAuth();
  const { userId, name: userName } = userInfo.user;

  // #region REDUX E DADOS
  const dispatch = useDispatch();
  const comments = useSelector(state => getPostComments(state, postId));

  const postInfo = useSelector(state => getPost(state, postId) || {});
  const count = postInfo.commentCount || 0;

  const groupData = useMemo(() => {
    return (
      postInfo.page ||
      (postInfo.author?.type === PROFILE_TYPES.group
        ? { ...postInfo.author, pageId: postInfo.author.profileId }
        : null)
    );
  }, [postInfo.page, postInfo.author]);

  // #endregion

  const pageId = groupData?.pageId || groupData?.id || groupData?.profileId;

  const defaultUser = {
    profileId: userId,
    name: userName,
    profileType: PROFILE_TYPES.user,
  };

  const storeKey = groupId ? 'NEW_POST_GROUP' : `COMMENT_${postId}`;

  const selectedProfile = useSelector(state => state.newPost.posts[storeKey]?.profile);
  const [isFeedManager, setIsFeedManager] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const checkAdminStatus = async () => {
      if (!pageId) {
        if (isMounted) setIsFeedManager(false);
        return;
      }

      try {
        const { data } = await axios.get(`/api/profile/getPageAdmOrManager?pageId=${pageId}`);

        if (isMounted) setIsFeedManager(!!data);
      } catch (error) {
        console.error('Erro ao verificar permissões de admin/gestor:', error);
        if (isMounted) setIsFeedManager(false);
      }
    };

    checkAdminStatus();

    return () => {
      isMounted = false;
    };
  }, [pageId]);

  let activeProfile = defaultUser;

  if (selectedProfile) {
    if (selectedProfile.profileType === PROFILE_TYPES.user) {
      activeProfile = defaultUser;
    } else if (selectedProfile.profileType === PROFILE_TYPES.group && groupData) {
      activeProfile = {
        profileId: groupData.pageId || groupData.id || groupData.profileId,
        name: groupData.name || groupData.title,
        profileType: PROFILE_TYPES.group,
        mediaId: groupData.mediaId,
        profilePhoto: groupData.profilePhoto || groupData.avatar,
      };
    }
  } else if (isFeedManager && groupData) {
    activeProfile = {
      profileId: groupData.pageId || groupData.id || groupData.profileId,
      name: groupData.name || groupData.title,
      profileType: PROFILE_TYPES.group,
      mediaId: groupData.mediaId,
      profilePhoto: groupData.profilePhoto || groupData.avatar,
    };
  }

  const page = comments.at(-1)?._pageComment ?? 1;

  const getCommentsHandler = async () => {
    fetchComments(page + 1);
  };

  const fetchComments = useCallback(
    async page => {
      setIsCommentLoading(true);

      try {
        await dispatch(getComments(postId, page, pageSize));
      } finally {
        setIsCommentLoading(false);
      }
    },
    [dispatch, pageSize, postId]
  );

  useEffect(() => {
    dispatch(resetComments(postId));
    fetchComments(1);
  }, [dispatch, fetchComments, postId]);

  const renderNewCommentEditor = () => {
    return (
      <div style={{ display: 'flex' }}>
        <AvatarWrapper>
          <ProfileSwitch storeKey={storeKey} groupData={groupData} origin="comment" />
          {/* <ProfileSwitch storeKey="NEW_POST_GROUP" groupData={groupData} /> */}
        </AvatarWrapper>
        <EditorWrapper>
          <CommentTextEditor
            placeholder={t('comments.leaveComment')}
            postId={postId}
            groupId={groupId}
            activeProfile={activeProfile}
          />
        </EditorWrapper>
      </div>
    );
  };

  return (
    <BorderComments>
      <CommentsContainer className={className}>
        {renderNewCommentEditor()}
        <RenderComments
          comments={comments}
          commentsCount={count}
          isCommentLoading={isCommentLoading}
          userId={userId}
          onGetComments={getCommentsHandler}
          groupId={groupId}
          groupData={groupData}
        />
      </CommentsContainer>
    </BorderComments>
  );
};

export default Comments;

const RenderComments = ({
  comments,
  commentsCount,
  isCommentLoading,
  userId,
  onGetComments,
  groupId,
  groupData,
}) => {
  const { t } = useTranslation();

  const totalSubComments = comments.reduce(
    (total, comment) => total + (comment.subCommentCount || 0),
    0
  );
  const effectiveCommentsCount = Math.max(commentsCount - totalSubComments, 0);
  const shouldNotFetchComments = commentsCount === 0 || comments.length >= effectiveCommentsCount;
  const isLoading = isCommentLoading && comments.length === 0;
  const loadMore =
    !shouldNotFetchComments && comments.length > 0 ? (
      <Button style={{ marginTop: 12 }} loading={isCommentLoading} onClick={onGetComments} block>
        {t('comments.moreComments')}
      </Button>
    ) : null;

  const loading = isLoading
    ? {
        indicator: <FontAwesomeIcon icon={faSpinnerThird} size="3x" spin />,
      }
    : false;

  const getCommentText = comment => {
    if (isJSON(comment.comment.description)) {
      const contentState = convertFromRaw(JSON.parse(comment.comment.description));
      return serializeToHtml(contentState);
    }
    return flow(mentionReplace, lineBrakeToBr)(comment.mentions, comment.comment.description);
  };

  const getSubCommentCount = comment => {
    if (comment.subCommentCount) {
      return comment.subCommentCount;
    }
    return 0;
  };

  return (
    <List
      css={commentsWrapper}
      loadMore={loadMore}
      dataSource={comments}
      split={false}
      rowKey={comment => comment.comment.commentId}
      loading={loading}
      renderItem={comment => (
        <List.Item>
          <Comment
            commentText={getCommentText(comment)}
            fullComment={comment}
            userId={userId}
            subCommentCount={getSubCommentCount(comment)}
            groupId={groupId}
            groupData={groupData}
          />
        </List.Item>
      )}
    />
  );
};
